import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Page } from '../theme/elements/Page';
import { Box, Button, MenuItem, TextField } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { clearDuplicateColorMap, clearPaginatedRequests } from 'state/reducers/pending-requests.reducer';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { getPendingRequestsByType } from 'services/pending-requests.service';
import { CalendarPickerInput } from 'components/FormCreator/inputs/CalendarPickerInput';
import { PageHeaderActionsRow } from 'theme/elements/Content';
import { PendingRequestsTables } from 'sections/pendingRequests/PendingRequestsTables';
import { useParams } from 'react-router-dom';
import { pendingRequestsTypes, ALL_BALANCE_ADDITIONS } from 'constants/requests.constants';
import { Order } from 'components/table/types';
import { DEFAULT_PAGE_SIZE, DEFAULT_SORT_ORDER } from 'constants/pagination.constants';

const DEFAULT_SORT: Order = DEFAULT_SORT_ORDER;

// Types that support pagination (those with API endpoints)
const PAGINATED_TYPES = [
	pendingRequestsTypes.BANK_ACCOUNTS,
	pendingRequestsTypes.BALANCES,
	pendingRequestsTypes.DIGITAL_BALANCES_REQUESTS,
	pendingRequestsTypes.BENEFICIARIES,
	pendingRequestsTypes.PAYMENTS,
	pendingRequestsTypes.WITHDRAWAL_SUPPORT,
	pendingRequestsTypes.WITHDRAWAL_BO_APPROVED,
	pendingRequestsTypes.BALANCES_BO_REQUESTS,
];

const SORT_BY_BY_TYPE: Partial<Record<pendingRequestsTypes, string>> = {
	[pendingRequestsTypes.BANK_ACCOUNTS]: 'created_at',
	[pendingRequestsTypes.BALANCES]: 'created_at',
	[pendingRequestsTypes.DIGITAL_BALANCES_REQUESTS]: 'created_at',
	[pendingRequestsTypes.BENEFICIARIES]: 'updated_at',
	[pendingRequestsTypes.PAYMENTS]: 'created_at',
	[pendingRequestsTypes.WITHDRAWAL_SUPPORT]: 'created_at',
	[pendingRequestsTypes.WITHDRAWAL_BO_APPROVED]: 'created_at',
	[pendingRequestsTypes.BALANCES_BO_REQUESTS]: 'created_at',
};

const getSortByForType = (requestType: pendingRequestsTypes) => SORT_BY_BY_TYPE[requestType] || 'created_at';

export const PendingRequestsApp = () => {
	const { type, companyId } = useParams();
	const parsedCompanyId = companyId ? parseInt(companyId, 10) : undefined;
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);

	// Initial loading state for global loader
	const [isInitialLoading, setIsInitialLoading] = useState(type === 'all' || !type);

	// Filter state
	const [filter, setFilter] = useState<{
		type: string;
		filterDate: any;
		companyId: number | undefined;
	}>({
		type: type || 'all',
		filterDate: null,
		companyId: parsedCompanyId,
	});

	// Pagination state per type
	const [pages, setPages] = useState<Record<string, number>>({});
	const sortStateByTypeRef = useRef<Record<string, { sortBy: string; sortOrder: Order }>>({});
	const getSortStateForType = (requestType: pendingRequestsTypes) =>
		sortStateByTypeRef.current[requestType] || {
			sortBy: getSortByForType(requestType),
			sortOrder: DEFAULT_SORT,
		};

	// Fetch function for a specific type
	const fetchRequestsByType = useCallback(
		async (
			requestType: pendingRequestsTypes,
			page: number,
			filterDate?: any,
			companyId?: number,
			sortOverride?: { sortBy: string; sortOrder: Order }
		) => {
			setPages((prev) => ({ ...prev, [requestType]: page }));

			// Convert to Date for API if needed
			const dateForApi = filterDate
				? filterDate instanceof Date
					? filterDate
					: new Date(filterDate)
				: undefined;
			const sortToUse = sortOverride || getSortStateForType(requestType);

			await dispatch(
				getPendingRequestsByType({
					type: requestType,
					offset: page * DEFAULT_PAGE_SIZE,
					limit: DEFAULT_PAGE_SIZE,
					sortBy: sortToUse.sortBy,
					sortOrder: sortToUse.sortOrder,
					filterDate: dateForApi,
					companyId,
				})
			);
		},
		[dispatch]
	);

	// Fetch data based on filter.type
	const fetchData = useCallback(async () => {
		const shouldShowGlobalLoader = filter.type === 'all';

		if (shouldShowGlobalLoader) {
			setIsInitialLoading(true);
		}

		try {
			if (filter.type === 'all') {
				// Fetch all paginated types in parallel
				await Promise.all(
					PAGINATED_TYPES.map((requestType) =>
						fetchRequestsByType(requestType, 0, filter.filterDate, filter.companyId)
					)
				);
			} else if (filter.type === ALL_BALANCE_ADDITIONS) {
				await Promise.all([
					fetchRequestsByType(pendingRequestsTypes.BALANCES, 0, filter.filterDate, filter.companyId),
					fetchRequestsByType(pendingRequestsTypes.DIGITAL_BALANCES_REQUESTS, 0, filter.filterDate, filter.companyId),
				]);
			} else if (PAGINATED_TYPES.includes(filter.type as pendingRequestsTypes)) {
				await fetchRequestsByType(filter.type as pendingRequestsTypes, 0, filter.filterDate, filter.companyId);
			}
		} finally {
			if (shouldShowGlobalLoader) {
				setIsInitialLoading(false);
			}
		}
	}, [filter.type, filter.filterDate, filter.companyId, fetchRequestsByType]);

	// Sync filter when URL params change (e.g. navigating from dashboard)
	useEffect(() => {
		const urlType = type || 'all';
		setFilter((prev) => (prev.type !== urlType ? { ...prev, type: urlType } : prev));
	}, [type]);

	// Initial fetch and on filter change
	useEffect(() => {
		fetchData();
	}, [fetchData]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			dispatch(clearPaginatedRequests());
			dispatch(clearDuplicateColorMap());
		};
	}, [dispatch]);

	// Filter handlers
	const handleTypeChange = (newType: string) => {
		setFilter((prev) => ({ ...prev, type: newType }));
		setPages({}); // Reset pagination
	};

	const handleDateChange = (val: any) => {
		setFilter((prev) => ({ ...prev, filterDate: val }));
		setPages({}); // Reset pagination
	};

	const handleClean = () => {
		setFilter((prev) => ({
			type: 'all',
			filterDate: null,
			companyId: prev.companyId,
		}));
		setPages({});
	};

	// Page change handler for tables
	const handlePageChange = useCallback(
		async (requestType: string, page: number) => {
			if (PAGINATED_TYPES.includes(requestType as pendingRequestsTypes)) {
				await fetchRequestsByType(
					requestType as pendingRequestsTypes,
					page,
					filter.filterDate,
					filter.companyId
				);
			}
		},
		[fetchRequestsByType, filter.filterDate, filter.companyId]
	);

	const handleSortChange = useCallback(
		async (requestType: pendingRequestsTypes, sortBy: string, sortOrder: Order) => {
			const nextSort = { sortBy, sortOrder };
			sortStateByTypeRef.current = { ...sortStateByTypeRef.current, [requestType]: nextSort };
			await fetchRequestsByType(requestType, 0, filter.filterDate, filter.companyId, nextSort);
		},
		[fetchRequestsByType, filter.filterDate, filter.companyId]
	);

	// Determine visible types
	const visibleTypes = useMemo(() => {
		if (filter.type === 'all') {
			return [...PAGINATED_TYPES];
		}
		if (filter.type === ALL_BALANCE_ADDITIONS) {
			return [pendingRequestsTypes.BALANCES, pendingRequestsTypes.DIGITAL_BALANCES_REQUESTS];
		}
		if (PAGINATED_TYPES.includes(filter.type as pendingRequestsTypes)) {
			return [filter.type as pendingRequestsTypes];
		}
		return [];
	}, [filter.type]);

	return (
		<Fragment>
			<Page
				title={t('page.pendingRequests.title')}
				loading={isInitialLoading}
				subTitle={
					<Box>
						<PageHeaderActionsRow>
							<TextField
								label={t('label.type')}
								select
								size="small"
								value={filter.type}
								color="primary"
								onChange={(e) => handleTypeChange(e.target.value)}
							>
								<MenuItem value={'all'}>{t('label.all')}</MenuItem>
								{PAGINATED_TYPES.flatMap((requestType) =>
									requestType === pendingRequestsTypes.DIGITAL_BALANCES_REQUESTS
										? [requestType, ALL_BALANCE_ADDITIONS]
										: [requestType]
								).map((value) => (
									<MenuItem key={value} value={value}>
										{t('page.pendingRequests.table.' + value + '.title')}
									</MenuItem>
								))}
							</TextField>
							<CalendarPickerInput
								label={t('label.date')}
								handleChange={handleDateChange}
								name="pendingRequestsCalendar"
								selectedDate={filter.filterDate || null}
							/>
							<Button variant="contained" onClick={handleClean}>
								{t('actions.name.clean')}
							</Button>
						</PageHeaderActionsRow>
					</Box>
				}
			>
				<PendingRequestsTables
					visibleTypes={visibleTypes}
					pages={pages}
					onPageChange={handlePageChange}
					onSortChange={handleSortChange}
					perPage={DEFAULT_PAGE_SIZE}
					onRefreshRequest={(requestType, page) => handlePageChange(requestType, page)}
				/>
			</Page>
		</Fragment>
	);
};
